import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, CalendarCheck, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { SERVICES } from "../data/siteData";
import { trackEvent } from "../lib/analytics";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/team", label: "Our Team" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const linkClass = (isActive) =>
  `px-3.5 py-2 rounded-full text-sm font-bold transition-colors duration-200 ${
    isActive ? "bg-lime text-navy" : "text-navy-light hover:text-teal-dark hover:bg-muted"
  }`;

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const servicesActive = location.pathname.startsWith("/services");
  const faqActive = location.pathname === "/faq" || location.pathname === "/insurance";

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-cream/95 backdrop-blur-sm border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center shrink-0 group">
          <img
            src="/thrive-logo.png"
            alt="Thrive Pediatric Therapy"
            className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        <nav data-testid="desktop-nav" className="hidden lg:flex items-center gap-1">
          <NavLink to="/" end data-testid="nav-link-home" className={({ isActive }) => linkClass(isActive)}>
            Home
          </NavLink>
          <NavLink to="/about" data-testid="nav-link-about" className={({ isActive }) => linkClass(isActive)}>
            About
          </NavLink>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-services-dropdown"
              className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-sm font-bold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2 ${
                servicesActive ? "bg-lime text-navy" : "text-navy-light hover:text-teal-dark hover:bg-muted"
              }`}
            >
              Services
              <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.5} />
            </DropdownMenuTrigger>
            <DropdownMenuContent data-testid="nav-services-menu" className="bg-cream rounded-2xl border-border p-2 min-w-[240px]">
              {SERVICES.map((s) => (
                <DropdownMenuItem key={s.slug} asChild className="rounded-xl cursor-pointer">
                  <Link to={`/services/${s.slug}`} data-testid={`nav-service-${s.slug}`} className="px-3 py-2.5 font-bold text-sm text-navy w-full">
                    {s.fullName}
                  </Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem asChild className="rounded-xl cursor-pointer border-t border-border mt-1 pt-1">
                <Link to="/services" data-testid="nav-service-all" className="px-3 py-2.5 font-extrabold text-sm text-teal-dark w-full">
                  View All Services
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <NavLink to="/team" data-testid="nav-link-our-team" className={({ isActive }) => linkClass(isActive)}>
            Our Team
          </NavLink>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-faq-dropdown"
              className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-sm font-bold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2 ${
                faqActive ? "bg-lime text-navy" : "text-navy-light hover:text-teal-dark hover:bg-muted"
              }`}
            >
              FAQ
              <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.5} />
            </DropdownMenuTrigger>
            <DropdownMenuContent data-testid="nav-faq-menu" className="bg-cream rounded-2xl border-border p-2 min-w-[220px]">
              <DropdownMenuItem asChild className="rounded-xl cursor-pointer">
                <Link to="/faq" data-testid="nav-faq-all" className="px-3 py-2.5 font-bold text-sm text-navy w-full">
                  All FAQs
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="rounded-xl cursor-pointer">
                <Link to="/insurance" data-testid="nav-faq-insurance" className="px-3 py-2.5 font-bold text-sm text-navy w-full">
                  Using Insurance
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <NavLink to="/contact" data-testid="nav-link-contact" className={({ isActive }) => linkClass(isActive)}>
            Contact
          </NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            onClick={() => trackEvent("book_now_click", { location: "navbar", channel: "contact" })}
            data-testid="nav-book-now-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-sunny text-navy font-extrabold text-sm px-6 py-3 rounded-full shadow-lg shadow-orange-500/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-sunny-dark hover:shadow-xl hover:shadow-orange-500/30 focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
          >
            <CalendarCheck className="w-4 h-4" strokeWidth={2.5} />
            Book Now
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-btn"
                aria-label="Open menu"
                className="lg:hidden w-11 h-11 rounded-full border border-border bg-white flex items-center justify-center text-navy transition-colors hover:bg-muted"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-cream w-80 overflow-y-auto">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <nav data-testid="mobile-nav" className="flex flex-col gap-2 mt-8">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-4 py-3 rounded-2xl font-bold transition-colors ${
                      location.pathname === link.to ? "bg-lime text-navy" : "text-navy hover:bg-muted"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <p className="px-4 pt-4 pb-1 text-[11px] font-extrabold uppercase tracking-[0.16em] text-navy-light/70">Services</p>
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-service-${s.slug}`}
                    className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-colors ${
                      location.pathname === `/services/${s.slug}` ? "bg-lime text-navy" : "text-navy hover:bg-muted"
                    }`}
                  >
                    {s.fullName}
                  </Link>
                ))}
                <p className="px-4 pt-4 pb-1 text-[11px] font-extrabold uppercase tracking-[0.16em] text-navy-light/70">Helpful Info</p>
                {[
                  { to: "/insurance", label: "Using Insurance", testId: "mobile-nav-link-insurance" },
                  { to: "/testimonials", label: "Testimonials", testId: "mobile-nav-link-testimonials" },
                  { to: "/start", label: "Not sure where to start?", testId: "mobile-nav-link-start" },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setOpen(false)}
                    data-testid={link.testId}
                    className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-colors ${
                      location.pathname + location.hash === link.to ? "bg-lime text-navy" : "text-navy hover:bg-muted"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/contact"
                  onClick={() => { setOpen(false); trackEvent("book_now_click", { location: "mobile_menu", channel: "contact" }); }}
                  data-testid="mobile-book-now-btn"
                  className="mt-4 inline-flex items-center justify-center gap-2 bg-sunny text-navy font-extrabold px-6 py-3.5 rounded-full shadow-lg shadow-orange-500/25"
                >
                  <CalendarCheck className="w-4 h-4" strokeWidth={2.5} />
                  Book Now
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
